import { useEffect, useState, type ReactNode } from 'react';

const appUrl = 'https://app.aiapplypilot.com';
const appHref = (placement: string) =>
  `${appUrl}/sign-in?utm_source=aiapplypilot.com&utm_medium=referral&utm_campaign=marketing_site&utm_content=${placement}`;

function Mark() {
  return (
    <svg className="mark" viewBox="0 0 48 48" aria-hidden="true">
      <rect width="48" height="48" rx="13" fill="currentColor" />
      <circle cx="24" cy="25" r="13" fill="none" stroke="#bfe6db" strokeWidth="1.5" />
      <path d="M8 19 40 10 29 39l-5-12z" fill="white" />
      <path
        d="m24 27 16-17-21 13"
        fill="none"
        stroke="#e1743d"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <span className="icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">{children}</svg>
    </span>
  );
}

function WorkspacePreview() {
  return (
    <a
      className="hero-art"
      href={appHref('hero-product-preview')}
      aria-label="Open AI Apply Pilot at app.aiapplypilot.com"
    >
      <div className="browser-bar" aria-hidden="true">
        <span className="browser-dots">
          <i />
          <i />
          <i />
        </span>
        <span className="browser-address">
          <svg viewBox="0 0 24 24">
            <path d="M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z" />
          </svg>
          app.aiapplypilot.com/dashboard
        </span>
        <span className="browser-action">Open app ↗</span>
      </div>
      <div
        className="workspace-preview"
        role="img"
        aria-label="Preview of the AI Apply Pilot application workspace with opportunities, applications, review tasks, and follow-ups"
      >
        <aside className="preview-sidebar" aria-hidden="true">
          <div className="preview-brand">
            <Mark />
            <span>Apply Pilot</span>
          </div>
          <div className="preview-nav">
            <span className="active">Overview</span>
            <span>Opportunities</span>
            <span>Applications</span>
            <span>AI review</span>
            <span>Documents</span>
          </div>
          <div className="preview-profile">
            <i>SA</i>
            <span>
              <b>Your workspace</b>
              <small>Free Basic plan</small>
            </span>
          </div>
        </aside>
        <div className="preview-main" aria-hidden="true">
          <div className="preview-kicker">
            <span>Workspace overview</span>
            <i>SA</i>
          </div>
          <h2>Keep every application moving.</h2>
          <p>See what needs attention and choose your next best step.</p>
          <div className="preview-metrics">
            <div>
              <span>Active applications</span>
              <strong>12</strong>
              <small>Across 4 stages</small>
            </div>
            <div>
              <span>Ready to review</span>
              <strong>4</strong>
              <small>Evidence checked</small>
            </div>
            <div>
              <span>Follow-ups due</span>
              <strong>3</strong>
              <small>This week</small>
            </div>
          </div>
          <div className="preview-detail-grid">
            <section className="preview-panel">
              <div className="preview-panel-head">
                <b>Application pipeline</b>
                <span>View all</span>
              </div>
              <div className="preview-row">
                <i className="company coral">N</i>
                <span>
                  <b>Product Designer</b>
                  <small>Northstar · Resume ready</small>
                </span>
                <em>Review</em>
              </div>
              <div className="preview-row">
                <i className="company green">V</i>
                <span>
                  <b>UX Researcher</b>
                  <small>Veridian · Applied</small>
                </span>
                <em className="soft">Tracking</em>
              </div>
              <div className="preview-row">
                <i className="company blue">A</i>
                <span>
                  <b>Design Lead</b>
                  <small>Atlas · Follow-up due</small>
                </span>
                <em className="warm">Action</em>
              </div>
            </section>
            <section className="preview-panel preview-focus">
              <span className="focus-label">Next best step</span>
              <strong>Review a role match</strong>
              <p>Compare the job description with your approved resume facts.</p>
              <div className="preview-progress">
                <span style={{ width: '78%' }} />
              </div>
              <small>Evidence review 78% complete</small>
              <span className="preview-button">Continue review →</span>
            </section>
          </div>
        </div>
      </div>
    </a>
  );
}

const plans = [
  {
    name: 'Free Basic',
    monthly: '$0',
    annual: '$0',
    annualTotal: '$0 / year',
    text: 'Build your profile and organize a focused search.',
    features: [
      'Candidate profile and application tracking',
      '5 job-description reports / month',
      '1 evidence-linked resume update preview / month',
      'Optimized DOCX/PDF export requires a paid plan',
    ],
  },
  {
    name: 'Career',
    monthly: '$19',
    annual: '$15',
    annualTotal: '$180 / year',
    text: 'Add guided analysis for an active job search.',
    features: [
      'Everything in Free Basic',
      '50 job-description reports / month',
      '20 evidence-linked resume updates / month',
    ],
  },
  {
    name: 'Pilot',
    monthly: '$39',
    annual: '$31',
    annualTotal: '$372 / year',
    text: 'For a broader search with more ongoing analysis.',
    features: [
      'Everything in Career',
      '300 job-description reports / month',
      '150 evidence-linked resume updates / month',
      'Eligible search-agent workflows as available',
    ],
    popular: true,
  },
  {
    name: 'Global Pro',
    monthly: '$69',
    annual: '$55',
    annualTotal: '$660 / year',
    text: 'For sustained, multi-track job searches.',
    features: [
      'Everything in Pilot',
      '1,000 job-description reports / month',
      '500 evidence-linked resume updates / month',
      'Priority support access',
    ],
  },
];

const faqs = [
  [
    'Does AI Apply Pilot submit applications for me?',
    'No. You review the information and remain responsible for every submission. The browser assistant is designed to pause for protected fields, verification, and final submission.',
  ],
  [
    'What does resume and job-description analysis do?',
    'It compares the materials you provide, highlights relevant alignment and gaps, and can help draft updates. It cannot guarantee interviews, employment, or that an employer will agree with its analysis.',
  ],
  [
    'What are paid AI updates?',
    'AI-assisted resume or application updates consume plan allowances or separately disclosed credits. You see the proposed change before choosing whether to use it. Enrollment stays closed until final billing details can be shown before purchase.',
  ],
  [
    'Can I search for roles internationally?',
    'The workspace is designed to support location and remote-work preferences. Source coverage, languages, and job availability vary by market; international discovery is being expanded progressively.',
  ],
  [
    'Is every feature available today?',
    'Core workspace capabilities are implemented and moving through production launch gates. Search-agent sources, geographic coverage, integrations, and higher-volume workflows are rolling out progressively and may depend on your plan or region.',
  ],
  [
    'How is my information protected?',
    'The platform is designed around least-privilege access, encrypted transport, isolated workspaces, audit records, and explicit user approval. No system is risk-free, so we also provide account security and privacy controls.',
  ],
];

export function App() {
  const [annual, setAnnual] = useState(false);
  const [announcement, setAnnouncement] = useState<{
    id: string;
    message: string;
    tone: 'INFO' | 'SUCCESS' | 'WARNING' | 'CRITICAL';
    dismissible: boolean;
  } | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void fetch('https://api.aiapplypilot.com/api/v1/platform/experience', {
      signal: controller.signal,
      credentials: 'omit',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((experience: { announcement?: typeof announcement } | null) => {
        const message = experience?.announcement ?? null;
        if (message && localStorage.getItem(`announcement:${message.id}`) !== 'dismissed') {
          setAnnouncement(message);
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  return (
    <>
      <a className="skip" href="#main">
        Skip to main content
      </a>
      {announcement && (
        <div className={`admin-banner ${announcement.tone.toLowerCase()}`} role="status">
          <div className="wrap">
            <span>{announcement.message}</span>
            {announcement.dismissible && (
              <button
                type="button"
                aria-label="Dismiss announcement"
                onClick={() => {
                  localStorage.setItem(`announcement:${announcement.id}`, 'dismissed');
                  setAnnouncement(null);
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
      <header className="site-header">
        <div className="nav wrap">
          <a className="brand" href="#top" aria-label="AI Apply Pilot home">
            <Mark />
            <span>AI Apply Pilot</span>
          </a>
          <nav aria-label="Primary navigation">
            <a href="#workflow">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#trust">Trust</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="nav-actions">
            <a className="nav-sign-in" href={appHref('header-sign-in')}>
              Sign in
            </a>
            <a className="button small" href={appHref('header-start-free')}>
              Start free <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </header>
      <main id="main">
        <section className="hero" id="top">
          <div className="wrap hero-grid">
            <div>
              <p className="eyebrow">The AI workspace for your job search</p>
              <h1>Turn job opportunities into focused applications.</h1>
              <p className="lede">
                Discover roles, compare each job with your resume, prepare evidence-based updates,
                and track every application in one clear workspace. AI assists; you review and
                decide.
              </p>
              <div className="actions">
                <a className="button" href={appHref('hero-primary')}>
                  Start free in the app <span aria-hidden="true">↗</span>
                </a>
                <a className="text-link" href="#workflow">
                  See how it works <span aria-hidden="true">↓</span>
                </a>
              </div>
              <p className="app-destination">
                <span>Live workspace</span>
                Opens <strong>app.aiapplypilot.com</strong> · No credit card for Free Basic
              </p>
            </div>
            <WorkspacePreview />
          </div>
        </section>

        <section className="app-path" aria-label="Path from product site to application">
          <div className="wrap app-path-inner">
            <p>
              <span className="path-label">One clear path</span>
              <strong>Explore at aiapplypilot.com</strong>
              <span className="path-arrow" aria-hidden="true">
                →
              </span>
              <strong>Work at app.aiapplypilot.com</strong>
            </p>
            <a className="text-link" href={appHref('domain-path')}>
              Open your workspace <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>

        <section className="proof" aria-label="Product principles">
          <div className="wrap proof-grid">
            <p>
              <strong>Human-approved</strong>
              <span>No automatic final submission</span>
            </p>
            <p>
              <strong>Evidence-first</strong>
              <span>Compare what your materials actually say</span>
            </p>
            <p>
              <strong>Built for focus</strong>
              <span>One organized application trail</span>
            </p>
          </div>
        </section>

        <section className="section" id="workflow">
          <div className="wrap">
            <div className="section-head">
              <p className="eyebrow">A practical workflow</p>
              <h2>From scattered tabs to a considered next step.</h2>
              <p>
                Use structured tools where they help. Keep judgment, sensitive answers, and final
                action where they belong—with you.
              </p>
            </div>
            <div className="steps">
              <article>
                <span className="number">01</span>
                <Icon>
                  <path d="M4 6h16M4 12h10M4 18h13" />
                </Icon>
                <h3>Set your direction</h3>
                <p>
                  Capture roles, locations, work preferences, experience, and the facts you want to
                  reuse.
                </p>
              </article>
              <article>
                <span className="number">02</span>
                <Icon>
                  <path d="M4 19V5h16v14M8 9h8M8 13h5" />
                </Icon>
                <h3>Compare the evidence</h3>
                <p>
                  Review how your resume relates to a job description, including useful matches and
                  visible gaps.
                </p>
              </article>
              <article>
                <span className="number">03</span>
                <Icon>
                  <path d="m5 12 4 4L19 6M5 21h14" />
                </Icon>
                <h3>Choose your updates</h3>
                <p>
                  Draft targeted changes with AI assistance when useful. Paid AI updates are always
                  presented for review.
                </p>
              </article>
              <article>
                <span className="number">04</span>
                <Icon>
                  <path d="M5 4h14v16H5zM9 8h6M9 12h6" />
                </Icon>
                <h3>Track the outcome</h3>
                <p>
                  Keep status, follow-ups, documents, and decisions connected throughout your
                  search.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="section tint">
          <div className="wrap feature-grid">
            <div className="analysis-art" aria-hidden="true">
              <div className="paper">
                <span className="paper-label">Role requirements</span>
                <div className="bar full" />
                <div className="bar mid" />
                <div className="match">
                  Relevant experience <b>Found</b>
                </div>
                <div className="bar full" />
                <div className="bar short" />
                <div className="gap">
                  Evidence to review <b>2 items</b>
                </div>
              </div>
              <div className="score">
                <strong>Clearer</strong>
                <span>comparison</span>
              </div>
            </div>
            <div>
              <p className="eyebrow">Truthful by design</p>
              <h2>Analysis that shows its work—not a promise it cannot make.</h2>
              <p className="big-copy">
                AI Apply Pilot helps surface language, skills, and evidence across the resume and
                job description you provide. It does not invent experience or predict an employer's
                decision.
              </p>
              <ul className="checks">
                <li>Keep source facts visible while reviewing suggestions</li>
                <li>Separate observed alignment from possible improvements</li>
                <li>Approve, change, or discard every proposed update</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="wrap global-grid">
            <div>
              <p className="eyebrow">Search beyond one job board</p>
              <h2>Designed for a wider search. Expanding responsibly.</h2>
              <p className="big-copy">
                Search-agent workflows help organize discovery around your criteria. International,
                language, and source coverage varies and is rolling out progressively—we show what a
                source supports instead of pretending every market works the same.
              </p>
              <div className="tags" aria-label="Search criteria examples">
                <span>Remote preferences</span>
                <span>Target locations</span>
                <span>Role families</span>
                <span>Source capability</span>
              </div>
            </div>
            <div
              className="globe"
              role="img"
              aria-label="Abstract globe representing expanding international search coverage"
            >
              <div className="longitude one" />
              <div className="longitude two" />
              <div className="latitude one" />
              <div className="latitude two" />
              <i className="pin a" />
              <i className="pin b" />
              <i className="pin c" />
            </div>
          </div>
        </section>

        <section className="section pricing" id="pricing">
          <div className="wrap">
            <div className="section-head centered">
              <p className="eyebrow">Simple starting points</p>
              <h2>Choose the pace that fits your search.</h2>
              <p>
                These are approved USD launch prices. Enrollment opens only after checkout and
                regional billing disclosures pass their production gates. Annual prices are monthly
                equivalents billed annually.
              </p>
              <div className="toggle" role="group" aria-label="Billing period">
                <button type="button" aria-pressed={!annual} onClick={() => setAnnual(false)}>
                  Monthly
                </button>
                <button type="button" aria-pressed={annual} onClick={() => setAnnual(true)}>
                  Annual <span>Save up to 21%</span>
                </button>
              </div>
            </div>
            <div className="plans">
              {plans.map((plan) => (
                <article className={plan.popular ? 'plan popular' : 'plan'} key={plan.name}>
                  {plan.popular && <p className="ribbon">Most popular</p>}
                  <h3>{plan.name}</h3>
                  <p>{plan.text}</p>
                  <div className="price">
                    <strong>{annual ? plan.annual : plan.monthly}</strong>
                    <span>/ month</span>
                  </div>
                  {annual && <p className="billing-note">{plan.annualTotal}, billed annually</p>}
                  <a
                    className={plan.popular ? 'button' : 'button outline'}
                    href={appHref(`pricing-${plan.name.toLowerCase().replaceAll(' ', '-')}`)}
                  >
                    {plan.monthly === '$0' ? 'Start free in the app' : 'Continue to the app'}
                  </a>
                  <ul>
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        ✓ <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section dark" id="trust">
          <div className="wrap trust-grid">
            <div>
              <p className="eyebrow">Trust is part of the product</p>
              <h2>Your career history deserves careful handling.</h2>
              <p className="big-copy">
                The platform is designed with scoped access, explicit approval, auditability, and
                privacy controls. We minimize what the browser assistant can access and stop at
                human-only steps.
              </p>
              <a className="text-link light" href="/privacy/">
                Read the privacy overview <span aria-hidden="true">↗</span>
              </a>
            </div>
            <div className="trust-cards">
              <article>
                <Icon>
                  <path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6zM9 12l2 2 4-5" />
                </Icon>
                <h3>Protected boundaries</h3>
                <p>
                  Encrypted transport, isolated workspace access, and short-lived authorization are
                  built into the system design.
                </p>
              </article>
              <article>
                <Icon>
                  <path d="M4 5h16v14H4zM8 9h8M8 13h5" />
                </Icon>
                <h3>Visible decisions</h3>
                <p>
                  Review source evidence and proposed changes; protected answers and final
                  submissions stay with you.
                </p>
              </article>
              <article>
                <Icon>
                  <path d="M12 3v18M3 12h18" />
                </Icon>
                <h3>Control and deletion</h3>
                <p>
                  Account security, data export, session, device, and privacy-request controls are
                  available in the workspace.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="section" id="faq">
          <div className="wrap faq-grid">
            <div>
              <p className="eyebrow">Good questions, clear answers</p>
              <h2>Before you take off.</h2>
              <p>We would rather set an accurate expectation than make a sweeping claim.</p>
            </div>
            <div className="faqs">
              {faqs.map(([question, answer]) => (
                <details key={question}>
                  <summary>
                    {question}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="cta">
          <div className="wrap cta-inner">
            <Mark />
            <div>
              <p className="eyebrow">Your next move can be deliberate</p>
              <h2>Bring your job search into one clear flight plan.</h2>
            </div>
            <a className="button pale" href={appHref('final-cta')}>
              Create your free workspace <span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'Organization',
                  '@id': 'https://aiapplypilot.com/#organization',
                  name: 'AI Apply Pilot',
                  url: 'https://aiapplypilot.com/',
                  logo: 'https://aiapplypilot.com/favicon.svg',
                  sameAs: [appUrl],
                },
                {
                  '@type': 'WebSite',
                  '@id': 'https://aiapplypilot.com/#website',
                  url: 'https://aiapplypilot.com/',
                  name: 'AI Apply Pilot',
                  publisher: { '@id': 'https://aiapplypilot.com/#organization' },
                },
                {
                  '@type': ['SoftwareApplication', 'Product'],
                  '@id': 'https://aiapplypilot.com/#product',
                  name: 'AI Apply Pilot',
                  applicationCategory: 'BusinessApplication',
                  operatingSystem: 'Web',
                  url: appUrl,
                  description:
                    'A human-controlled workspace for organizing job searches and reviewing resume and job-description analysis.',
                  brand: { '@id': 'https://aiapplypilot.com/#organization' },
                  offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                    url: appHref('structured-data'),
                  },
                },
                {
                  '@type': 'FAQPage',
                  mainEntity: faqs.map(([name, text]) => ({
                    '@type': 'Question',
                    name,
                    acceptedAnswer: { '@type': 'Answer', text },
                  })),
                },
              ],
            }),
          }}
        />
      </main>
      <footer>
        <div className="wrap footer-grid">
          <div>
            <a className="brand" href="#top">
              <Mark />
              <span>AI Apply Pilot</span>
            </a>
            <p>A human-controlled workspace for a more thoughtful job search.</p>
          </div>
          <div>
            <h2>Product</h2>
            <a href="#workflow">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#trust">Trust</a>
          </div>
          <div>
            <h2>Account</h2>
            <a href={appHref('footer-start-free')}>Start free</a>
            <a href={appHref('footer-sign-in')}>Sign in</a>
            <a href="/features/">Product readiness</a>
            <a href="/privacy/">Privacy</a>
            <a href="/security/">Security</a>
          </div>
          <div>
            <h2>Important</h2>
            <p>
              AI Apply Pilot does not guarantee interviews or employment and does not automatically
              submit final applications.
            </p>
            <a href="/terms/">Terms</a>
            <a href="/accessibility/">Accessibility</a>
          </div>
        </div>
        <div className="wrap legal">
          <span>© {new Date().getFullYear()} AI Apply Pilot</span>
          <span>aiapplypilot.com</span>
        </div>
      </footer>
    </>
  );
}
